import { Link, useSearchParams } from "react-router-dom";
import { useCrewDay, useOfflineQueue, type CrewDay, type SyncResult } from "../../api/crew";
import { SiteMap } from "../dashboard/SiteMap";
import { dayChoices, nextStop, stopDone } from "./crewDay";
import { PendingBadge } from "./PendingBadge";
import { NextStopCard, SiteCard, StopCard } from "./StopCards";
import "../../styles/crew.css";

export { PendingBadge } from "./PendingBadge";

type View = "today" | "map";

/** My work: the crew's sites, or one planned day's stops in order, with the
 * same set on a map. Reads want a signal; the writes on the site screens do
 * not, and the badge here says how many are waiting to be sent. On a phone
 * the list and the map are two tabs; on a desk they sit side by side. */
export default function CrewPage() {
  const [params, setParams] = useSearchParams();
  const day = params.get("day");
  const view: View = params.get("view") === "map" ? "map" : "today";
  const crewDay = useCrewDay(day);
  const queue = useOfflineQueue();

  function choose(next: { day?: string; view?: View }) {
    const merged = new URLSearchParams(params);
    if (next.day !== undefined) {
      merged.set("day", next.day);
    }
    if (next.view !== undefined) {
      if (next.view === "map") {
        merged.set("view", "map");
      } else {
        merged.delete("view");
      }
    }
    setParams(merged);
  }

  return (
    <div className="crew-page">
      {crewDay.isPending ? <p className="crew-state">Loading your work…</p> : null}
      {crewDay.isError ? (
        <p className="notice bad">Your work could not be loaded: {crewDay.error.message}. Check the signal and try again.</p>
      ) : null}
      {crewDay.data ? (
        <CrewDayView
          data={crewDay.data}
          view={view}
          pending={queue.pending}
          refusals={queue.refusals}
          onFlush={() => void queue.flush()}
          onDay={(value) => choose({ day: value })}
          onView={(value) => choose({ view: value })}
        />
      ) : null}
    </div>
  );
}

export function CrewDayView({
  data,
  view,
  pending,
  refusals,
  onFlush,
  onDay,
  onView,
}: {
  data: CrewDay;
  view: View;
  pending: number;
  refusals: readonly SyncResult[];
  onFlush: () => void;
  onDay: (day: string) => void;
  onView: (view: View) => void;
}) {
  const { pills, earlier } = dayChoices(data.days, data.today);
  const selected = data.selected ?? "all";
  const next = nextStop(data.stops);
  const done = data.stops.filter(stopDone).length;
  const positions = new Map(data.points.map((p) => [p.id, { lat: p.lat, lon: p.lon }]));

  return (
    <>
      <header className="crew-head">
        <div className="crew-title">
          <h1>My work</h1>
          {data.crew ? <p className="crew-name">{data.crew}</p> : null}
        </div>
        <PendingBadge pending={pending} onFlush={onFlush} />
        <div className="day-pills" role="group" aria-label="Planned days">
          {pills.map((pill) => (
            <button key={pill.value} type="button" className="day-pill" aria-pressed={selected === pill.value} onClick={() => onDay(pill.value)}>
              {pill.label}
            </button>
          ))}
          {earlier.length > 0 ? (
            <select
              className="day-pill day-earlier"
              aria-label="An earlier day"
              value={earlier.some((d) => d.value === selected) ? selected : ""}
              onChange={(e) => e.target.value && onDay(e.target.value)}
            >
              <option value="">Earlier…</option>
              {earlier.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </header>

      {refusals.length > 0 ? (
        <div className="notice bad">
          The office could not apply {refusals.length === 1 ? "one change" : `${refusals.length} changes`}:
          <ul className="crew-refusals">
            {refusals.map((r) => (
              <li key={r.client_id}>{r.error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={view === "map" ? "crew-body show-map" : "crew-body"}>
        <div className="crew-list">
          {data.stops.length > 0 ? (
            <>
              {next ? (
                <NextStopCard stop={next} position={positions.get(next.site.id) ?? null} total={data.stops.length} />
              ) : (
                <section className="next-stop all-done" aria-label="Next stop">
                  <span className="next-stop-label">{done === data.stops.length ? "Day done" : "Nothing left to visit"}</span>
                  <h2 className="next-stop-name">
                    {done === data.stops.length
                      ? "Every stop on this day is done."
                      : `${done} done, ${data.stops.length - done} not done. Every stop is recorded.`}
                  </h2>
                </section>
              )}
              <p className="section-label">
                {done} of {data.stops.length} done{selected === data.today ? " today" : ""}
              </p>
              {data.stops.map((stop) => (
                <StopCard key={stop.seq} stop={stop} current={next !== null && stop.seq === next.seq} />
              ))}
            </>
          ) : data.sites.length > 0 ? (
            <>
              <p className="section-label">
                {data.sites.length} site{data.sites.length === 1 ? "" : "s"} assigned
              </p>
              {data.sites.map((site) => (
                <SiteCard key={site.id} site={site} />
              ))}
            </>
          ) : (
            <div className="crew-empty">
              <strong>{data.selected ? "Nothing planned for this day." : "Nothing assigned to you yet."}</strong>
              {data.selected ? " Choose Everything to see every site you hold." : " The office assigns sites; they appear here."}
            </div>
          )}
        </div>
        <div className="crew-map">
          {data.points.length > 0 ? (
            <SiteMap sites={data.points.map((p) => ({ ...p, label: "" }))} colours={{}} linkBase="/crew/sites" />
          ) : (
            <div className="dashed-box crew-map-empty">No site on this list has a position yet.</div>
          )}
          {data.unplaceable > 0 ? (
            <p className="crew-note">
              {data.unplaceable} site{data.unplaceable === 1 ? "" : "s"} without a position not shown
            </p>
          ) : null}
        </div>
      </div>

      <nav className="crew-tabbar" aria-label="My work">
        <button type="button" aria-pressed={view === "today"} onClick={() => onView("today")}>
          Today
        </button>
        <button type="button" aria-pressed={view === "map"} onClick={() => onView("map")}>
          Map
        </button>
        <Link to="/help">Help</Link>
      </nav>
    </>
  );
}
